import { useState, useCallback, useContext, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import {
  TOAST_IDS,
  ToastType,
  DeveloperTrackItem,
} from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { createLeadTracksBulk } from "../api";
import { LEAD_TRACKS_QUERY_KEY } from "./use-lead-tracks.hook";
import { RowFormApi, RowFormResult, TrackErrors } from "../types";

interface UseCreateTrackProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export const useCreateTrack = ({ onSuccess, onClose }: UseCreateTrackProps) => {
  const { lead } = useContext(LeadPageContext);
  const leadAny = lead?.data?.lead as any;
  const leadUId = leadAny?.uid;

  const [createdTracks, setCreatedTracks] = useState<DeveloperTrackItem[]>([
    {
      id: crypto.randomUUID(),
      leadUId: leadUId || "",
      developerUId: "",
      developerUserUId: "",
    },
  ]);

  const [trackErrors, setTrackErrors] = useState<TrackErrors>({});
 
  const rowFormApiRef = useRef<Map<string, RowFormApi>>(new Map());
  const [loadingFormTrackIds, setLoadingFormTrackIds] = useState<Set<string>>(
    new Set()
  );

  const registerRowFormApi = useCallback(
    (id: string, api: RowFormApi | undefined) => {
      if (api) {
        rowFormApiRef.current.set(id, api);
      } else {
        rowFormApiRef.current.delete(id);
      }
    },
    []
  );

  const setTrackFormLoading = useCallback((id: string, loading: boolean) => {
    setLoadingFormTrackIds((prev) => {
      const isLoading = prev.has(id);
      if (loading === isLoading) return prev;
      const next = new Set(prev);
      if (loading) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleAddTrack = useCallback(() => {
    setCreatedTracks((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        leadUId: leadUId || "",
        developerUId: "",
        developerUserUId: "",
      },
    ]);
  }, [leadUId]);

  const handleDeleteTrack = useCallback((id: string) => {
    setCreatedTracks((prev) => prev.filter((track) => track.id !== id));
    // Also clear error for deleted track
    setTrackErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
     rowFormApiRef.current.delete(id);
    setLoadingFormTrackIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleTrackChange = useCallback(
    (id: string, developerUId: string, developerUserUId: string) => {
      setCreatedTracks((prev) =>
        prev.map((track) =>
          track.id === id ? { ...track, developerUId, developerUserUId } : track
        )
      );
      // Clear errors for this track when values change
      setTrackErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[id]) {
          if (developerUId) delete newErrors[id]?.developer;
          if (!newErrors[id]?.developer) {
            delete newErrors[id];
          }
        }
        return newErrors;
      });
    },
    []
  );

  // Validate tracks and return true if valid
  const validateTracks = useCallback((): boolean => {
    const errors: TrackErrors = {};
    let hasErrors = false;

    createdTracks.forEach((track) => {
      if (!track.developerUId) {
        errors[track.id] = { developer: true };
        hasErrors = true;
      }
    });

    setTrackErrors(errors);
    return !hasErrors;
  }, [createdTracks]);

  // Mutation to create tracks via bulk endpoint
  const { mutate: createTrack, isLoading: isSubmitting } = useMutation({
    mutationFn: async (
      bulkData: {
        leadUId: string;
        developerUId: string;
        developerUserUId?: string;
        formData?: Record<string, unknown>;
      }[]
    ) => {
      return createLeadTracksBulk(bulkData);
    },
    onSuccess: () => {
      showToast({
        type: ToastType.SUCCESS,
        message: "Tracks created successfully",
        toastId: TOAST_IDS.ADD_TRACK_SUCCESS,
        options: { position: "bottom-left", autoClose: 3000 },
      });

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: [LEAD_TRACKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["lead-activity"] });

      onSuccess?.();
      onClose?.();

      // Reset state
      setCreatedTracks([
        {
          id: crypto.randomUUID(),
          leadUId: leadUId || "",
          developerUId: "",
          developerUserUId: "",
        },
      ]);
      setTrackErrors({});
      rowFormApiRef.current.clear();
      setLoadingFormTrackIds(new Set());
    },
    onError: (error: any) => {
      showToast({
        type: ToastType.ERROR,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create tracks",
        toastId: TOAST_IDS.ADD_TRACK_ERROR,
        options: { position: "bottom-left", autoClose: 3000 },
      });
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!leadUId) {
      console.error("Missing required data: leadUId");
      return;
    }

    // Validate before submitting
    if (!validateTracks()) {
      return;
    }

    // Get valid tracks
    const validTracks = createdTracks.filter((t) => t.developerUId);

    if (validTracks.length === 0) {
      return;
    }

    // Check for duplicate developers
    const developerIds = validTracks.map((t) => t.developerUId);
    const uniqueDeveloperIds = new Set(developerIds);
    if (developerIds.length !== uniqueDeveloperIds.size) {
      showToast({
        type: ToastType.ERROR,
        message: "You cannot create multiple tracks for the same developer",
        toastId: TOAST_IDS.ADD_TRACK_ERROR,
        options: { position: "bottom-left", autoClose: 3000 },
      });
      return;
    }

   
    const rowResults = await Promise.all(
      validTracks.map(async (t) => {
        const rowApi = rowFormApiRef.current.get(t.id);
        const result: RowFormResult = rowApi
          ? await rowApi()
          : { ok: true };
        return { track: t, result };
      })
    );

    if (rowResults.some(({ result }) => !result.ok)) {
      showToast({
        type: ToastType.ERROR,
        message:
          "Please complete the required presentation form for the highlighted developer track(s)",
        toastId: TOAST_IDS.ADD_TRACK_ERROR,
        options: { position: "bottom-left", autoClose: 3000 },
      });
      return;
    }

    const bulkData = rowResults.map(({ track: t, result }) => ({
      leadUId: leadUId,
      developerUId: t.developerUId,
      developerUserUId: t.developerUserUId || undefined,
      ...(result.formData && { formData: result.formData }),
    }));

    createTrack(bulkData);
  }, [createTrack, validateTracks, createdTracks, leadUId]);

 
  const isValid =
    createdTracks.some((t) => t.developerUId) &&
    loadingFormTrackIds.size === 0;

  // Check if we can add a new track (all existing tracks have a developer selected)
  const canAddNewTrack = createdTracks.every((t) => t.developerUId);

  // Reset function to clear all tracks
  const resetTracks = useCallback(() => {
    setCreatedTracks([
      {
        id: crypto.randomUUID(),
        leadUId: leadUId || "",
        developerUId: "",
        developerUserUId: "",
      },
    ]);
    setTrackErrors({});
    rowFormApiRef.current.clear();
    setLoadingFormTrackIds(new Set());
  }, [leadUId]);

  return {
    createdTracks,
    handleAddTrack,
    handleDeleteTrack,
    handleTrackChange,
    resetTracks,
    isSubmitting,
    handleSubmit,
    isValid,
    canAddNewTrack,
    trackErrors,
    registerRowFormApi,
    setTrackFormLoading,
  };
};

export default useCreateTrack;
